<script setup lang="ts">
/**
 * Aventross AGM (Audited) — 원본 합본 1파일(# AGM - Audited Companies [without no.9])을 Stage 로 나눠 보여 준다.
 *   생성 문서는 1개 그대로(템플릿 1파일) — 화면만 단계별. 문서 컴포넌트(docs/Av*Doc.vue)는 템플릿 글자 그대로 생성(gen_vue).
 *   사람·표 행 반복은 useRepeat(값은 fill).
 */
import { computed, ref, watch } from 'vue';
import type { Component } from 'vue';
import AvDrwDoc from './docs/AvDrwDoc.vue';
import AvNoticeDoc from './docs/AvNoticeDoc.vue';
import AvShorterNoticeDoc from './docs/AvShorterNoticeDoc.vue';
import AvMinutesDoc from './docs/AvMinutesDoc.vue';
import AvAttendanceDoc from './docs/AvAttendanceDoc.vue';
import AvDisclosureDoc from './docs/AvDisclosureDoc.vue';
import AvDeclarationDoc from './docs/AvDeclarationDoc.vue';
import AvControllerNoticeDoc from './docs/AvControllerNoticeDoc.vue';
import AvMinutesRepDoc from './docs/AvMinutesRepDoc.vue';
import AvAnnexADoc from './docs/AvAnnexADoc.vue';
import AvAnnexBDoc from './docs/AvAnnexBDoc.vue';
import AvAnnexCDoc from './docs/AvAnnexCDoc.vue';

interface StageDef { en: string; short: string; docs: Array<{ label: string; comp: Component }> }

const STAGES: StageDef[] = [
  {
    en: 'Resolutions & Notice', short: 'DRW / Notice',
    docs: [
      { label: "Directors' Resolutions in Writing", comp: AvDrwDoc },
      { label: 'Notice of Annual General Meeting', comp: AvNoticeDoc },
      { label: 'Agreement by Members to Shorter Notice', comp: AvShorterNoticeDoc },
    ],
  },
  {
    en: 'Minutes & Attendance', short: 'Minutes',
    docs: [
      { label: 'Minutes of Annual General Meeting', comp: AvMinutesDoc },
      { label: 'Attendance', comp: AvAttendanceDoc },
    ],
  },
  {
    en: 'Disclosure of Interests (per director)', short: 'Disclosure',
    docs: [{ label: 'Disclosure of Interests in Transactions, Property, Offices etc.', comp: AvDisclosureDoc }],
  },
  {
    en: 'Declaration & Notice to Controllers', short: 'Controllers',
    docs: [
      { label: 'Declaration of Register of Controllers, Nominee Directors and Nominee Shareholders', comp: AvDeclarationDoc },
      { label: 'Notice to Registrable Controller — s.386AG(2)(a) & 386AIA(1) (per controller)', comp: AvControllerNoticeDoc },
    ],
  },
  {
    en: 'Minutes by Representative of Holding Company', short: 'Rep Minutes',
    docs: [{ label: 'Minutes of AGM by Representative of Holding Company', comp: AvMinutesRepDoc }],
  },
  {
    en: 'Registers (Annex A / B / C)', short: 'Annex A–C',
    docs: [
      { label: 'Annex A — Register of Registrable Controllers', comp: AvAnnexADoc },
      { label: 'Annex B — Register of Nominee Director', comp: AvAnnexBDoc },
      { label: 'Annex C — Register of Nominee Shareholder', comp: AvAnnexCDoc },
    ],
  },
];

const currentStage = ref(1);
const totalStages = STAGES.length;
function go(n: number) { if (n >= 1 && n <= totalStages) currentStage.value = n; }
const view = computed(() => {
  let n = 0;
  return STAGES.map((st, i) => ({ ...st, no: i + 1, docs: st.docs.map((d) => ({ ...d, no: (n += 1) })) }));
});
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));
const paperRef = ref<HTMLElement | null>(null);
watch(currentStage, () => paperRef.value?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }));

/* zoom */
const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }

function gatherLocalState() { return { currentStage: currentStage.value }; }
function restoreLocalState(s: { currentStage?: number } | null) {
  if (s?.currentStage && s.currentStage >= 1 && s.currentStage <= totalStages) currentStage.value = s.currentStage;
}
defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="av-pkg">
    <div class="stepper">
      <button v-for="st in view" :key="st.no" type="button" class="step" :class="{ active: currentStage === st.no, done: currentStage > st.no }" @click="go(st.no)">
        <span class="step-no">{{ currentStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.short }}</small></span>
      </button>
    </div>

    <div class="zoom-bar">
      <div class="doc-zoom">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>

    <div ref="paperRef" class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <!-- v-show: 단계를 바꿔도 입력 칸이 등록된 채로 남아 진행률·값이 유지된다 -->
      <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage {{ st.no }}</span><span class="stage-t">{{ st.en }}</span></div>
        <div v-for="d in st.docs" :key="d.no" class="pkg-doc">
          <div class="pkg-doclabel"><span class="ic">📄</span><span class="nm">{{ d.label }}</span><span class="no">Part {{ d.no }} / {{ totalDocs }}</span></div>
          <div class="simple-frame"><component :is="d.comp" /></div>
        </div>
      </section>
    </div>

    <div class="stage-nav">
      <button v-if="currentStage > 1" type="button" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ totalStages }}</span>
      <button v-if="currentStage < totalStages" type="button" class="nav-btn primary" @click="go(currentStage + 1)">Next →</button>
      <button v-else type="button" class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<style scoped>
.av-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20,30,60,0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; }
.step-txt small { font-size: 10px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 18px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; }
.doc-zoom-btn { min-width: 36px; font-size: 15px; }
.doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.stage { width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; }
.stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }
.pkg-doc { width: 100%; }
.pkg-doc + .pkg-doc { margin-top: 36px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 12px; }
.pkg-doclabel .ic { font-size: 14px; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 16px; width: 794px; max-width: 100%; margin: 36px auto 12px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px 20px; cursor: pointer; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn:disabled { opacity: 0.55; cursor: default; }
.nav-count { font-size: 12.5px; font-weight: 800; color: #8a97b5; }
</style>
